import { Editor2DController } from '@/application/Editor2DController';
import { container } from '@/infrastructure/container';
import { createFabricAdapters } from '@/infrastructure/engine2d/createFabricAdapters';
import type { IEngine2DInitParams } from '@/application/ports/IEngine2DInitParams';
import type { TSide } from '@/domain/shared/TSide';

// Vue-side entries to the Application layer:
// - mountEditor2D(side, canvas, params): create + cache the editor on
//   canvas mount. Called from useCanvasLifecycle inside onMounted.
// - useEditor2D(side): pure lookup, returns null before mount. Called
//   from every consumer (components, composables) that needs the
//   editor without owning its lifecycle.
//
// Both hand out the Editor2DController directly — Vue reaches through
// its sub-controllers (editor.text, editor.viewport, editor.printZone…).
// No per-method pass-through binder; refactoring an adapter no longer
// touches this file.

type TCacheEntry = {
    canvasElement: HTMLCanvasElement;
    controller: Editor2DController;
    disposeAdapters: () => void;
};

const _cache = new Map<TSide, TCacheEntry>();

function mountEditor2D(side: TSide, canvasElement: HTMLCanvasElement, initParams: IEngine2DInitParams): Editor2DController {
    const cached = _cache.get(side);
    if (cached && cached.canvasElement === canvasElement) {
        return cached.controller;
    }
    if (cached) {
        cached.controller.dispose();
        cached.disposeAdapters();
    }
    const {
        cuttingShapeEngine,
        dispose: disposeAdapters,
        engine,
        exporter,
        printZoneEngine,
        viewportEngine,
    } = createFabricAdapters(side, canvasElement, initParams);
    const { entityRepository } = container;
    const controller = new Editor2DController({
        cuttingShapeEngine,
        cuttingShapeRepository: container.cuttingShapeRepository,
        engine,
        entityRepository,
        exporter,
        printZoneEngine,
        productImageRepository: container.productImageRepository,
        productStateRepository: container.productStateRepository,
        selection: entityRepository,
        side,
        templateConfigurationRepository: container.templateConfigurationRepository,
        viewportEngine,
    });
    _cache.set(side, { canvasElement, controller, disposeAdapters });
    return controller;
}

function useEditor2D(side: TSide): Editor2DController | null {
    return _cache.get(side)?.controller ?? null;
}

function _disposeEditor2D(side: TSide): void {
    const cached = _cache.get(side);
    if (!cached) {
        return;
    }
    cached.controller.dispose();
    cached.disposeAdapters();
    _cache.delete(side);
}

export { _disposeEditor2D, mountEditor2D, useEditor2D };
